import React, { useState, useEffect, useRef } from 'react';
import {
    Stamp, Download, Loader2, ArrowLeft, Type, Image as ImageIcon,
    Settings, AlignCenter, AlignLeft, AlignRight, AlignJustify, File
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { addWatermark } from '../utils/pdfWatermark';
import * as pdfjsLib from 'pdfjs-dist';
import JSZip from 'jszip';

const WatermarkPdf = () => {
    const [files, setFiles] = useState([]); // Array of files
    const [isProcessing, setIsProcessing] = useState(false);

    // Filter Options
    const [options, setOptions] = useState({
        type: 'text', // 'text' | 'image'
        text: 'CONFIDENTIAL',
        imageFile: null,
        opacity: 0.5,
        rotation: 45,
        size: 50, // Font size or Image scale
        color: '#ff0000',
        position: 'center'
    });

    const canvasRef = useRef(null);

    // Initial file load
    const handleFileChange = async (e) => {
        const selectedFiles = Array.from(e.target.files).filter(f => f.type === 'application/pdf');
        if (selectedFiles.length > 0) {
            setFiles(selectedFiles);
            // Preview the first file
            generatePreview(selectedFiles[0]);
        }
    };

    const handleOptionChange = (key, value) => {
        setOptions(prev => ({ ...prev, [key]: value }));
    };

    // Render first page to canvas for preview
    const generatePreview = async (pdfFile) => {
        if (!pdfFile || !canvasRef.current) return;

        try {
            const arrayBuffer = await pdfFile.arrayBuffer();
            const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
            const page = await pdf.getPage(1);

            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            const viewport = page.getViewport({ scale: 0.8 }); // Preview scale

            canvas.height = viewport.height;
            canvas.width = viewport.width;

            await page.render({ canvasContext: context, viewport }).promise;

            updateWatermarkPreview(pdfFile, options);

        } catch (error) {
            console.error("Error previewing PDF:", error);
        }
    };

    // Use effect to update preview when options change (debounced)
    useEffect(() => {
        if (files.length > 0) {
            const timer = setTimeout(() => {
                updateWatermarkPreview(files[0]);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [options, files]);

    // Guard: Reset size if it's invalid for the current type
    useEffect(() => {
        if (options.type === 'image' && options.size > 5) {
            setOptions(prev => ({ ...prev, size: 0.5 }));
        } else if (options.type === 'text' && options.size < 5) {
            setOptions(prev => ({ ...prev, size: 50 }));
        }
    }, [options.type]);

    const updateWatermarkPreview = async (originalFile) => {
        if (!originalFile || !canvasRef.current) return;

        try {
            setIsProcessing(true);
            const watermarkedBlob = await addWatermark(originalFile, options);

            // Render this blob
            const arrayBuffer = await watermarkedBlob.arrayBuffer();
            const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
            const page = await pdf.getPage(1);

            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            // Fit canvas to container
            const containerWidth = canvas.parentElement.clientWidth - 40; // padding
            const unscaledViewport = page.getViewport({ scale: 1 });
            const scale = Math.min(1, containerWidth / unscaledViewport.width);
            const viewport = page.getViewport({ scale });

            canvas.height = viewport.height;
            canvas.width = viewport.width;

            await page.render({ canvasContext: context, viewport }).promise;
        } catch (err) {
            console.error(err);
            alert(err.message || "Failed to generate preview");
        } finally {
            setIsProcessing(false);
        }
    };


    const handleDownload = async () => {
        if (files.length === 0) return;
        setIsProcessing(true);

        try {
            // Single File Download
            if (files.length === 1) {
                const watermarkedBlob = await addWatermark(files[0], options);
                const url = URL.createObjectURL(watermarkedBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `watermarked_${files[0].name}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
            // Batch Download (ZIP)
            else {
                const zip = new JSZip();

                for (let i = 0; i < files.length; i++) {
                    const file = files[i];
                    const watermarkedBlob = await addWatermark(file, options);
                    zip.file(`watermarked_${file.name}`, watermarkedBlob);
                }

                const content = await zip.generateAsync({ type: "blob" });
                const url = URL.createObjectURL(content);
                const link = document.createElement('a');
                link.href = url;
                link.download = "watermarked_files.zip";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error(error);
            alert("Failed to process PDFs");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="container animate-fade-in" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)' }}>
            <div style={{ marginBottom: '1rem' }}>
                <Link to="/tools/pdf" className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}>
                    <ArrowLeft size={16} /> Back to PDF Tools
                </Link>
            </div>

            <div style={{ display: 'flex', gap: '2rem', flex: 1, minHeight: 0 }}>
                {/* Sidebar Controls */}
                <div className="glass-card" style={{ width: '350px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
                        <Stamp className="text-primary" /> Properties
                    </h2>

                    {files.length === 0 ? (
                        <div style={{ textAlign: 'center', margin: 'auto' }}>
                            <input type="file" id="up" accept=".pdf" multiple onChange={handleFileChange} hidden />
                            <label htmlFor="up" className="btn btn-primary">Upload PDFs</label>
                            <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', opacity: 0.7 }}>Select one or multiple files</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {/* File List Info */}
                            <div style={{ background: 'rgba(0,0,0,0.05)', padding: '0.75rem', borderRadius: '0.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
                                    <File size={16} />
                                    {files.length} File{files.length > 1 ? 's' : ''} Selected
                                </div>
                                <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '0.25rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {files[0].name} {files.length > 1 && `+ ${files.length - 1} more`}
                                </div>
                                <button
                                    onClick={() => setFiles([])}
                                    style={{ color: 'var(--primary)', fontSize: '0.8rem', textDecoration: 'underline', marginTop: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                >
                                    Clear Selection
                                </button>
                            </div>

                            {/* Type Toggle */}
                            <div style={{ display: 'flex', background: 'rgba(255,255,255,0.5)', borderRadius: '0.5rem', padding: '0.25rem' }}>
                                <button
                                    className={`btn ${options.type === 'text' ? 'btn-primary' : ''}`}
                                    style={{ flex: 1, boxShadow: 'none' }}
                                    onClick={() => setOptions(prev => ({ ...prev, type: 'text', size: 50 }))}
                                >
                                    <Type size={16} style={{ marginRight: '0.5rem' }} /> Text
                                </button>
                                <button
                                    className={`btn ${options.type === 'image' ? 'btn-primary' : ''}`}
                                    style={{ flex: 1, boxShadow: 'none' }}
                                    onClick={() => setOptions(prev => ({ ...prev, type: 'image', size: 0.5 }))}
                                >
                                    <ImageIcon size={16} style={{ marginRight: '0.5rem' }} /> Image
                                </button>
                            </div>

                            {/* Content Input */}
                            {options.type === 'text' ? (
                                <div>
                                    <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Watermark Text</label>
                                    <input
                                        type="text"
                                        value={options.text}
                                        onChange={(e) => handleOptionChange('text', e.target.value)}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border)' }}
                                    />
                                    <div style={{ marginTop: '0.5rem' }}>
                                        <label style={{ fontSize: '0.9rem' }}>Color</label>
                                        <input
                                            type="color"
                                            value={options.color}
                                            onChange={(e) => handleOptionChange('color', e.target.value)}
                                            style={{ width: '100%', height: '40px', marginTop: '0.25rem' }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Upload Image</label>
                                    <input
                                        type="file"
                                        accept="image/png, image/jpeg, image/jpg"
                                        onChange={(e) => {
                                            if (e.target.files[0]) handleOptionChange('imageFile', e.target.files[0]);
                                        }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            )}

                            <hr style={{ borderColor: 'var(--border)' }} />

                            {/* Sliders */}
                            <div>
                                <label style={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
                                    Opacity <span>{Math.round(options.opacity * 100)}%</span>
                                </label>
                                <input
                                    type="range" min="0.1" max="1" step="0.1"
                                    value={options.opacity}
                                    onChange={(e) => handleOptionChange('opacity', parseFloat(e.target.value))}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            <div>
                                <label style={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
                                    Rotation <span>{options.rotation}°</span>
                                </label>
                                <input
                                    type="range" min="-180" max="180" step="15"
                                    value={options.rotation}
                                    onChange={(e) => handleOptionChange('rotation', parseInt(e.target.value))}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            <div>
                                <label style={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
                                    Size / Scale <span>{options.size}</span>
                                </label>
                                <input
                                    type="range" min={options.type === 'text' ? "10" : "0.1"} max={options.type === 'text' ? "150" : "2.0"} step={options.type === 'text' ? "5" : "0.1"}
                                    value={options.size}
                                    onChange={(e) => handleOptionChange('size', parseFloat(e.target.value))}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            {/* Position Grid */}
                            <div>
                                <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Position</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                                    {['top-left', 'top-right', 'center', 'bottom-left', 'bottom-right'].map(pos => (
                                        <button
                                            key={pos}
                                            onClick={() => handleOptionChange('position', pos)}
                                            className={`btn ${options.position === pos ? 'btn-primary' : 'btn-secondary'}`}
                                            style={{ fontSize: '0.8rem', padding: '0.5rem' }}
                                        >
                                            {pos.replace('-', ' ')}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Main Preview Area */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <header className="flex-center" style={{ flexDirection: 'column', marginBottom: '1rem', textAlign: 'center' }}>
                        <h1 style={{ fontSize: '2rem' }}>Watermark PDF</h1>
                        <p>Add security and branding to your document</p>
                    </header>

                    <div style={{
                        flex: 1,
                        width: '100%',
                        background: 'rgba(0,0,0,0.05)',
                        borderRadius: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        {files.length > 0 ? (
                            <>
                                <canvas
                                    ref={canvasRef}
                                    style={{
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                        maxHeight: '100%',
                                        maxWidth: '100%'
                                    }}
                                />
                                {isProcessing && (
                                    <div style={{
                                        position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                                        zIndex: 10
                                    }}>
                                        <Loader2 className="animate-spin" size={48} color="var(--primary)" />
                                        <p style={{ marginTop: '1rem', fontWeight: 600 }}>
                                            {files.length > 1 ? `Processing ${files.length} Files...` : "Updating Preview..."}
                                        </p>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                                <Stamp size={64} style={{ opacity: 0.2 }} />
                                <p>Upload PDF(s) to start</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            {files.length > 0 && (
                <div className="glass-card flex-center" style={{ marginTop: '1rem', padding: '1rem', animation: 'fadeIn 0.5s ease-out' }}>
                    <button
                        className="btn btn-primary"
                        onClick={handleDownload}
                        disabled={isProcessing}
                        style={{ padding: '0.75rem 3rem', fontSize: '1.2rem' }}
                    >
                        {isProcessing ? <Loader2 className="animate-spin" /> :
                            <>
                                <Download />
                                {files.length > 1 ? `Download All as ZIP (${files.length})` : 'Download Watermarked PDF'}
                            </>
                        }
                    </button>
                </div>
            )}
        </div>
    );
};

export default WatermarkPdf;
